import type { AdminDomainSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { NumberBadge } from '@components/shared/number-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { withForm } from '@components/ui/form/form';
import { FormDescription } from '@components/ui/form/shared.component';
import { useAdminAvailableSubdomainsQuery } from '@queries/admin-domains.query';
import { useStore } from '@tanstack/react-form';
import { mapToSelectOptions } from '@utils/select.util';
import { CheckIcon, EditIcon, XIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { adminDomainFormOptions } from './admin-domain-form-options';

type OptionSource = {
  id: string;
  name: string;
  archived?: boolean;
};

export const AdminDomainSubdomains = withForm({
  ...adminDomainFormOptions,
  props: {
    disableEdit: false,
    domain: undefined as unknown as AdminDomainSelectDto,
    isBusy: false,
    isEditing: false,
    onDiscard: undefined as unknown as () => void,
    onEdit: undefined as unknown as () => void,
    onSave: undefined as unknown as () => void,
  },
  render: function Render({ disableEdit, domain, form, isBusy, isEditing, onDiscard, onEdit, onSave }) {
    const [, setSelectedDomainId] = useQueryState('domainId', parseAsString);

    const { data: availableSubdomainsData, isPending: isLoadingOptions } = useAdminAvailableSubdomainsQuery();

    const optionSources = useMemo<OptionSource[]>(() => {
      const map = new Map<string, OptionSource>();

      for (const subdomain of domain.subdomains ?? []) {
        map.set(subdomain.id, {
          archived: subdomain.archived,
          id: subdomain.id,
          name: subdomain.name,
        });
      }

      for (const candidate of availableSubdomainsData?.domains ?? []) {
        if (candidate.id === domain.id || map.has(candidate.id)) {
          continue;
        }
        map.set(candidate.id, {
          archived: candidate.archived,
          id: candidate.id,
          name: candidate.name,
        });
      }

      return Array.from(map.values()).sort((first, second) => first.name.localeCompare(second.name));
    }, [availableSubdomainsData?.domains, domain.id, domain.subdomains]);

    const subdomainOptions = mapToSelectOptions(optionSources);

    const totalDependents =
      domain.subdomains?.reduce((sum, subdomain) => sum + (subdomain.totalDependencies ?? 0), 0) ?? 0;

    const currentSubdomains = useStore(form.store, (state) => state.values.subdomains ?? []);

    const parentDomainId = useStore(form.store, (state) => state.values.parentDomainId);
    const canEdit = !parentDomainId;

    return (
      <Tile
        className="border border-gray-200 shadow-none"
        header={{
          actions: isEditing ? (
            <div className="flex gap-1">
              <IconButton
                disabled={isBusy}
                onClick={onDiscard}
                size="md"
                icon={<XIcon size={16} />}
                variant="secondary"
              />

              <IconButton
                disabled={isBusy}
                onClick={onSave}
                size="md"
                icon={<CheckIcon size={16} />}
                variant="primary"
              />
            </div>
          ) : undefined,
          iconButton: isEditing
            ? undefined
            : {
                disabled: disableEdit,
                disabledTooltip: 'Save or discard changes before editing',
                icon: <EditIcon size={16} />,
                onClick: onEdit,
              },
          title: (
            <div className="flex items-center gap-2">
              <NumberBadge
                size="md"
                value={currentSubdomains.length}
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Subdomains</span>
                <div className="flex items-center gap-1">
                  <TooltipComponent
                    side="top"
                    trigger={
                      <span className="text-xs font-normal border-dashed border-b text-gray-500">
                        Used by {totalDependents === 1 ? '1 entity' : `${totalDependents} entities`}
                      </span>
                    }
                  >
                    Entities (assessments, technology use domains, etc.) that are using these subdomains.
                  </TooltipComponent>
                </div>
              </div>
            </div>
          ),
        }}
        hoverReveal={!isEditing && !disableEdit}
      >
        {(isEditing || (domain.subdomains?.length ?? 0) > 0) && (
          <div className="flex flex-col gap-3">
            {isEditing ? (
              <div className="flex flex-col gap-3">
                <form.AppForm>
                  <form.AppField name="subdomains">
                    {(field) => (
                      <>
                        <field.SelectMultipleField
                          disabled={!canEdit || isLoadingOptions}
                          options={subdomainOptions}
                          placeholder={
                            canEdit ? 'Select subdomains...' : 'Remove the parent domain to manage subdomains.'
                          }
                        />
                        <FormDescription>
                          Subdomain options are limited to domains that are not archived and do not have any parent
                          domain or subdomains.
                        </FormDescription>
                      </>
                    )}
                  </form.AppField>
                </form.AppForm>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {domain.subdomains?.map((subdomain) => (
                  <button
                    key={subdomain.id}
                    className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors hover:bg-gray-50"
                    onClick={() => void setSelectedDomainId(subdomain.id)}
                    type="button"
                  >
                    <span className="text-left font-medium">{subdomain.name}</span>
                    {subdomain.archived && (
                      <Badge
                        size="xs"
                        variant="warning"
                      >
                        Archived
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Tile>
    );
  },
});
